import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostBinding,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { Subject, Subscription, interval, takeUntil } from 'rxjs';

import { UpdatesRequiredService } from 'app/layout/common/updates-required/updates-required.service';
import { UpdateRequiredOrder } from 'app/layout/common/updates-required/updates-required.types';
import { MailboxService } from 'app/modules/admin/mailbox/mailbox.service';
import { Mail } from 'app/modules/admin/mailbox/mailbox.types';
import { ChatService } from 'app/modules/admin/chat/chat.service';
import { Chat } from 'app/modules/admin/chat/chat.types';
import { OrdersService } from 'app/modules/admin/orders/orders.service';
import { operatesAsTrader } from 'app/core/user/domain-roles';

/**
 * One renderable row in the marquee. Every source (chat, email,
 * order, problem order) lowers itself into the same shape so the
 * template can `*ngFor` a single array.
 */
interface StreamItem {
    /** Stable trackBy id; also doubles as the dismiss key for event tiles. */
    id: string;
    /** Which feed the row came from — drives the chip label + click target. */
    kind: 'update' | 'chat' | 'email' | 'order_event';
    /** Leading badge — order id, count, "+N". */
    badge?: string;
    /** Headline — order name / chat partner / email sender. */
    title: string;
    /** Short status caption — "Past Due", "Completed", "Email". */
    caption?: string;
    /** Trailing meta — due date / snippet / time-ago. */
    meta?: string;
    /** Material icon name. */
    icon: string;
    /** Color accent. */
    accent: 'success' | 'info' | 'warning' | 'danger' | 'neutral';
    /**
     * Sort key. Higher = closer to the head of the tape.
     * Events use their arrival timestamp (ms since epoch). Problem
     * orders use a negative key so they always sort BEHIND any
     * fresh event — they're persistent state that should only fill
     * the tape when there's nothing newer to surface.
     */
    sortKey: number;
}

/**
 * Internal record for an event-style tile (chat / email / order
 * change). Wraps a `StreamItem` with the lifecycle data the rebuild
 * step needs: when it arrived, when it should auto-expire, how many
 * times the same source has pinged (for fold-up rendering).
 */
interface EventTile {
    item: StreamItem;
    /** ms timestamp when the first occurrence of this event arrived. */
    arrivedAt: number;
    /** ms timestamp when this tile should auto-expire. */
    expiresAt: number;
    /** Times this dismissKey has fired since the tile appeared. */
    count: number;
    /**
     * Optional "after which item id should this be spliced" hint
     * used by the next-slot injection so a brand-new tile appears
     * in the visible portion of the marquee instead of off-screen
     * to the left at the sorted head.
     */
    pinAfterId?: string | null;
    /** ms timestamp when the pin hint expires (back to natural sort). */
    pinExpiresAt?: number;
}

/**
 * Top-bar consolidated live ticker tape — EVENT-driven.
 *
 * Earlier iterations rendered the standing state of every feed
 * (every unread chat, every problem order, an "X unread" mail
 * pill). That made the tape feel like a static dashboard — a row
 * for a 3-day-old unread message looked identical to a row for a
 * message that just arrived 10 seconds ago.
 *
 * This version flips the model: the tape is a stream of MOMENTS.
 *
 *   • Chat: sticky toast in quick-chat is the primary alert
 *     (`newMessageNotification$`). Live feed does not duplicate
 *     chat tiles from that ping (alert fatigue). Leftover chat
 *     tiles still dismiss when `chats$` shows unreadCount → 0.
 *   • Email tile appears on `newEmailReceived$` / `newEmailsDetected$`.
 *     It disappears when `emailUpdated$` emits the same thread with
 *     `unread: false`.
 *   • Order tile appears on `orderUpdated$` / `orderCreated$` —
 *     direct push from `OrdersService`. There's no "read" notion for
 *     status changes; the tile expires on the timeout.
 *   • Problem-order rows (past due, missing fields, etc.) still
 *     render but at a NEGATIVE sortKey so events always lead the
 *     tape. Problem rows only get screen time when the event buffer
 *     is empty/sparse — they're the "fill the silence" feed.
 *
 * Dedup + fold-up: every event tile is keyed by a `dismissKey`
 * (`chat:<convId>`, `email:<threadId>`, `order:<id>:<status>`). A
 * second arrival on the same key bumps a `count` instead of stacking
 * a duplicate row, and refreshes the expiry so a chatty sender
 * stays on the tape as long as they keep typing.
 */
@Component({
    selector: 'topbar-live-feed',
    standalone: true,
    imports: [CommonModule, MatIconModule, MatTooltipModule],
    templateUrl: './topbar-live-feed.component.html',
    styleUrls: ['./topbar-live-feed.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopbarLiveFeedComponent implements OnInit, AfterViewInit, OnDestroy {
    /**
     * Reserved compact flag — currently a no-op (the marquee
     * naturally adapts to whatever horizontal space it's given).
     * Left in the API so callers don't have to be re-wired if we
     * later want a denser variant (e.g. dropping the meta segment).
     */
    @Input() compact = false;

    @HostBinding('class.topbar-live-feed--host') readonly hostClass = true;

    /** Track element used by `findRightmostVisibleId()`. */
    @ViewChild('trackEl', { static: false }) trackEl?: ElementRef<HTMLElement>;

    /** Consolidated stream rendered into the marquee. */
    streamItems: StreamItem[] = [];

    /** "now" stamp for time-ago formatting / expiry sweeps. */
    now: number = Date.now();

    private readonly TIME_AGO_MS = 30_000;
    private readonly MAX_PER_SOURCE = 12;
    private readonly MAX_TOTAL = 30;

    /**
     * Event tiles live for up to an hour by default. Chosen to
     * cover "someone stepped away from their desk and came back"
     * scenarios — short enough that the tape doesn't become a wall
     * of stale things, long enough that a brief bathroom break
     * doesn't wipe context.
     */
    private readonly EVENT_TTL_MS = 60 * 60 * 1000;

    /** How long a manual position pin stays active. */
    private readonly INJECT_PIN_MS = 60_000;

    private readonly destroy$ = new Subject<void>();
    private timeAgoSub: Subscription | null = null;

    /** Standing snapshot of problem orders (Updates Required). */
    private _updates: UpdateRequiredOrder[] = [];
    /** Has the updates fetch settled at least once? */
    private _updatesLoaded = false;

    /**
     * Per-chat-id record of the previous unreadCount value, used
     * to detect "transitioned from unread to read" so we can
     * auto-dismiss the matching event tile.
     */
    private _lastChatUnread = new Map<string, number>();

    /**
     * Unified buffer of every event tile currently on the tape.
     * Map key = `dismissKey` so a repeated event for the same
     * source (same chat, same email thread, same order+status)
     * folds into the existing tile instead of duplicating.
     */
    private _eventTiles = new Map<string, EventTile>();

    /**
     * Snapshot of each order's assignment fields, used to detect
     * transitions on the `orders$` stream. Without this we'd have
     * no way to tell "trader_printer_id just changed" from "trader_
     * printer_id was always like that" — assignment changes don't
     * have their own dedicated push event, they ride the broad
     * orders$ update like everything else.
     *
     * First emission primes the map and emits NOTHING — every order
     * comes in looking like a "new" assignment otherwise.
     */
    private _assignmentSnapshot = new Map<
        string,
        {
            traderId: string | null;
            printerId: string | null;
            resourceId: string | null;
            assigneeUserId: string | null;
        }
    >();
    private _assignmentSnapshotPrimed = false;

    /**
     * Current viewer identity — used for role-gating assignment
     * tiles. Admin sees every assignment/unassignment; Trader sees
     * only the events involving themselves (assigned to me /
     * unassigned from me). Resolved on init from `userData`.
     */
    private _currentUserId: string | null = null;
    private _currentUserRole: string = '';
    private _currentUser: any = null;
    private _operatesAsTrader: boolean = false;

    /**
     * Wall-clock of the last successful `getUpdateIssueOrders()`
     * refresh. Used to throttle the per-loop self-refresh so
     * that an unusually short marquee duration can't pound the
     * backend faster than once per `UPDATES_MIN_REFRESH_MS`.
     */
    private _lastUpdatesRefresh = 0;

    /**
     * Minimum wall-clock gap between two self-refreshes. 30s is
     * short enough that a resolved problem-order falls off the
     * tape within "one or two loops", long enough that it can't
     * become a continuous-poll loop even if the marquee duration
     * is dropped to 5s for a demo.
     */
    private readonly UPDATES_MIN_REFRESH_MS = 30_000;

    /** Unbind handle for the marquee-iteration listener. */
    private _animationIterationUnbind: (() => void) | null = null;

    constructor(
        private cdr: ChangeDetectorRef,
        private ngZone: NgZone,
        private router: Router,
        private updatesRequiredService: UpdatesRequiredService,
        private mailboxService: MailboxService,
        private chatService: ChatService,
        private ordersService: OrdersService,
    ) {}

    ngOnInit(): void {
        this.resolveCurrentUser();
        this.subscribeUpdates();
        this.subscribeChatEvents();
        this.subscribeEmailEvents();
        this.subscribeOrderEvents();
        this.subscribeAssignmentTransitions();
        this.startTick();
    }

    /**
     * One-shot read of the logged-in user from localStorage so
     * the assignment-event filter can decide what to surface for
     * Admin vs Trader. We treat missing/garbage data as Admin
     * (least restrictive) — the worst case is showing a few extra
     * tiles, not hiding one the user expected to see.
     */
    private resolveCurrentUser(): void {
        try {
            const raw = typeof localStorage !== 'undefined'
                ? localStorage.getItem('userData')
                : null;
            if (!raw) return;
            const data = JSON.parse(raw);
            this._currentUser = data;
            this._currentUserId = data?.id != null ? String(data.id) : null;
            this._currentUserRole = String(data?.role || '').trim();
            this._operatesAsTrader = !!data?.operates_as_trader;
        } catch {
            // Leave defaults — see method comment for fallback rationale.
        }
    }

    private isAdmin(): boolean { return this._currentUserRole === 'Admin'; }
    private isTrader(): boolean {
        if (this._currentUser) {
            return operatesAsTrader(this._currentUser);
        }
        return this._currentUserRole === 'Trader' || this._operatesAsTrader;
    }

    /**
     * Wire up the marquee self-refresh.
     *
     * Each time the CSS `tlfMarquee` animation completes a full
     * pass, the browser fires an `animationiteration` event on
     * the track element. We use that as the "loop boundary" —
     * before we start showing the same list a second time,
     * re-fetch the problem-orders snapshot from the backend so
     * resolved items disappear and freshly-flagged ones appear.
     *
     * `animation-play-state: paused` (set on hover/focus by the
     * SCSS) suspends the animation altogether, which means no
     * iteration events fire while the user is actively reading
     * the tape. Refreshing under a stationary cursor would be
     * jarring; this gives us the right behavior for free.
     */
    ngAfterViewInit(): void {
        const trackEl = this.trackEl?.nativeElement;
        if (!trackEl) return;

        const handler = (evt: AnimationEvent) => {
            if (evt.animationName !== 'tlfMarquee') return;
            this.refreshUpdatesIfDue();
        };

        // Run outside Angular's zone — `animationiteration` can
        // fire frequently (every loop), and we don't want each
        // event to trigger a change-detection cycle. The refresh
        // method handles its own zone re-entry via the service
        // subscription, which re-emits inside the zone.
        this.ngZone.runOutsideAngular(() => {
            trackEl.addEventListener('animationiteration', handler);
            this._animationIterationUnbind = () =>
                trackEl.removeEventListener('animationiteration', handler);
        });
    }

    ngOnDestroy(): void {
        this.timeAgoSub?.unsubscribe();
        this._animationIterationUnbind?.();
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Throttled re-fetch of the problem-orders list. The
     * `BehaviorSubject` push inside `getUpdateIssueOrders()` is
     * what flows through to our `issueOrders$` subscription and
     * re-runs `rebuildStream()`, so resolved orders fall off
     * and newly-flagged orders appear without any local work
     * here.
     */
    private refreshUpdatesIfDue(): void {
        const now = Date.now();
        if (now - this._lastUpdatesRefresh < this.UPDATES_MIN_REFRESH_MS) {
            return;
        }
        this._lastUpdatesRefresh = now;
        this.updatesRequiredService
            .getUpdateIssueOrders()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                // Errors are already logged inside the service;
                // we explicitly suppress here so an offline blip
                // doesn't break the iteration listener.
                error: () => {},
            });
    }

    // ─────────────────────────────────────────────────────────────────
    // trackBy + click routing
    // ─────────────────────────────────────────────────────────────────
    trackById = (_: number, item: StreamItem) => item.id;

    onItemClick(item: StreamItem): void {
        // Route the click to the source that owns this row.
        // The tile's own click is also an implicit "I'm handling
        // this" — drop the tile from the event buffer so it doesn't
        // keep cycling once the user has navigated to it.
        switch (item.kind) {
            case 'update':
            case 'order_event':
                this.router.navigate(['/orders/list']);
                break;
            case 'email':
                this.router.navigate(['/orders/list'], {
                    queryParams: { workspace: 'email' },
                });
                break;
            case 'chat':
                this.router.navigate(['/orders/list'], {
                    queryParams: { workspace: 'chat' },
                });
                break;
        }

        if (item.kind !== 'update' && this._eventTiles.has(item.id)) {
            this._eventTiles.delete(item.id);
            this.rebuildStream();
        }
    }

    // ─────────────────────────────────────────────────────────────────
    // Source subscriptions
    // ─────────────────────────────────────────────────────────────────

    /**
     * Problem-orders feed. These are STANDING state — past-due,
     * missing fields, etc. They render with a negative sortKey so
     * the event tiles always lead the tape; problem rows only get
     * surface area when the event buffer is empty or sparse.
     */
    private subscribeUpdates(): void {
        this.updatesRequiredService.issueOrders$
            .pipe(takeUntil(this.destroy$))
            .subscribe((orders: UpdateRequiredOrder[]) => {
                this._updates = (orders || []).slice(0, this.MAX_PER_SOURCE);
                if (this._updates.length > 0) this._updatesLoaded = true;
                this.rebuildStream();
            });

        this.updatesRequiredService
            .getUpdateIssueOrders()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => { this._updatesLoaded = true; this.rebuildStream(); },
                error: () => { this._updatesLoaded = true; this.rebuildStream(); },
            });
    }

    /**
     * Chat events.
     *
     * Sticky toast (quick-chat) is the primary actionable alert for new
     * chat/SMS messages (`newMessageNotification$` → "View").
     *
     * Live feed intentionally does NOT create chat tiles from that ping
     * anymore — dual surfaces caused alert fatigue. This subscription
     * only watches `chats$` so any leftover chat tiles still auto-dismiss
     * when unreadCount returns to 0 (user opened the conversation).
     */
    private subscribeChatEvents(): void {
        this.chatService.chats$
            .pipe(takeUntil(this.destroy$))
            .subscribe((chats: Chat[]) => {
                const list = chats || [];

                // Detect "was unread, now read" transitions and
                // drop the matching event tile.
                let dismissed = false;
                for (const chat of list) {
                    if (!chat?.id) continue;
                    const prev = this._lastChatUnread.get(chat.id) ?? 0;
                    const curr = chat.unreadCount ?? 0;
                    if (prev > 0 && curr === 0) {
                        const key = `chat:${chat.id}`;
                        if (this._eventTiles.delete(key)) dismissed = true;
                    }
                    this._lastChatUnread.set(chat.id, curr);
                }

                if (dismissed) this.rebuildStream();
            });
    }

    /**
     * Email events.
     *
     * Each Nylas webhook / polled detection drops a `Mail` object
     * into the tape. Dedup is by `thread_id` so a chatty thread
     * folds to one tile with a `+N` badge rather than stacking
     * five rows for the same conversation. `emailUpdated$` with
     * `unread: false` dismisses the tile — i.e. opening the email
     * makes its tile fall off the tape, same lifecycle as chat.
     */
    private subscribeEmailEvents(): void {
        if (!this.mailboxService) return;

        this.mailboxService.newEmailReceived$
            ?.pipe(takeUntil(this.destroy$))
            .subscribe((mail: Mail) => this.handleEmailArrival(mail));

        this.mailboxService.newEmailsDetected$
            ?.pipe(takeUntil(this.destroy$))
            .subscribe((batch: { count: number; emails: Mail[] }) => {
                if (!batch?.emails?.length) return;
                // Newest-first so the most recent thread is what
                // the user sees enter the viewport first.
                const ordered = [...batch.emails].sort((a, b) => {
                    const ta = a?.date ? new Date(a.date as any).getTime() : 0;
                    const tb = b?.date ? new Date(b.date as any).getTime() : 0;
                    return tb - ta;
                });
                for (const mail of ordered) this.handleEmailArrival(mail);
            });

        this.mailboxService.emailUpdated$
            ?.pipe(takeUntil(this.destroy$))
            .subscribe((mail: Mail) => {
                if (!mail || mail.unread !== false) return;
                const key = this.emailDismissKey(mail);
                if (!key) return;
                if (this._eventTiles.delete(key)) this.rebuildStream();
            });
    }

    private handleEmailArrival(mail: Mail): void {
        if (!mail || mail.unread === false) return;
        const key = this.emailDismissKey(mail);
        if (!key) return;
        const senderName =
            mail.from?.name?.trim() ||
            mail.from?.email ||
            'New email';
        const subject = (mail.subject || '').trim() || '(no subject)';
        this.pushEventTile({
            dismissKey: key,
            kind: 'email',
            icon: 'mail',
            accent: 'info',
            title: senderName,
            caption: 'Email',
            meta: this.truncate(subject, 90),
        });
    }

    private emailDismissKey(mail: Mail): string | null {
        const thread = (mail?.thread_id || '').toString();
        if (thread) return `email:thread:${thread}`;
        const id = (mail?.id || '').toString();
        return id ? `email:msg:${id}` : null;
    }

    /**
     * Order events from `OrdersService`. `orderUpdated$` covers
     * status changes (Production → Completed, etc.); `orderCreated$`
     * covers brand-new orders. Both ride the same event buffer with
     * dedup by `order:<id>:<action>` so saving the same status twice
     * in quick succession doesn't double-render.
     */
    private subscribeOrderEvents(): void {
        if (!this.ordersService?.orderUpdated$) return;

        this.ordersService.orderUpdated$
            .pipe(takeUntil(this.destroy$))
            .subscribe((order: any) => this.handleOrderEvent(order, 'updated'));

        const created$: any = (this.ordersService as any).orderCreated$;
        if (created$ && typeof created$.subscribe === 'function') {
            created$
                .pipe(takeUntil(this.destroy$))
                .subscribe((order: any) => this.handleOrderEvent(order, 'created'));
        }
    }

    /**
     * Watch the orders cache for assignment-field transitions.
     *
     * The `orderUpdated$` push covers the *acting* user (the
     * admin who clicked Assign sees their tile immediately via
     * the optimistic emit). But the *receiving* user — a Trader
     * sitting on their dashboard who just had an order routed to
     * them — only learns about the change when the websocket
     * refreshes their `orders$` cache.
     *
     * This subscription closes that loop: diff the previous vs
     * current assignment fields and synthesize the appropriate
     * tile. Dedup is by `order:<id>:<action>` which matches the
     * key the optimistic emit uses, so when both paths fire on
     * the assigner's machine the second arrival just folds into
     * the existing tile instead of stacking a duplicate row.
     */
    private subscribeAssignmentTransitions(): void {
        if (!this.ordersService?.orders$) return;

        this.ordersService.orders$
            .pipe(takeUntil(this.destroy$))
            .subscribe((orders: any[]) => {
                if (!Array.isArray(orders)) return;

                // Prime on the first emission so we don't fire a
                // tile for every order in the initial cache load.
                if (!this._assignmentSnapshotPrimed) {
                    for (const order of orders) {
                        if (order?.id == null) continue;
                        this._assignmentSnapshot.set(
                            String(order.id),
                            this.readAssignmentState(order),
                        );
                    }
                    this._assignmentSnapshotPrimed = true;
                    return;
                }

                for (const order of orders) {
                    if (order?.id == null) continue;
                    const key = String(order.id);
                    const prev = this._assignmentSnapshot.get(key);
                    const curr = this.readAssignmentState(order);
                    this._assignmentSnapshot.set(key, curr);

                    if (!prev) {
                        // Brand-new order in the cache mid-session
                        // (e.g. a freshly-created order). Treat its
                        // initial assignment as a tile-worthy event
                        // only if it lands on the current user —
                        // otherwise the row would compete with the
                        // `orderCreated$` tile from the create event.
                        if (curr.assigneeUserId && this.matchesCurrentUser(curr.assigneeUserId)) {
                            this.emitAssignmentTile(order, null, curr);
                        }
                        continue;
                    }

                    if (this.assignmentChanged(prev, curr)) {
                        this.emitAssignmentTile(order, prev, curr);
                    }
                }
            });
    }

    /**
     * Reduce an order's mutually-exclusive assignment fields to a
     * single normalized state. The "primary" assignee user id is
     * what the role gate compares against the current user — a
     * `resource_id` (DP house, not a user) deliberately leaves it
     * null so DP routing never counts as "assigned to me".
     */
    private readAssignmentState(order: any): {
        traderId: string | null;
        printerId: string | null;
        resourceId: string | null;
        assigneeUserId: string | null;
    } {
        const traderId = order?.trader_printer_id != null
            ? String(order.trader_printer_id)
            : null;
        const printerId = order?.printer_id != null
            ? String(order.printer_id)
            : null;
        const resourceId = order?.resource_id != null
            ? String(order.resource_id)
            : null;
        const assigneeUserId = traderId || printerId || null;
        return { traderId, printerId, resourceId, assigneeUserId };
    }

    private assignmentChanged(
        a: { traderId: string | null; printerId: string | null; resourceId: string | null },
        b: { traderId: string | null; printerId: string | null; resourceId: string | null },
    ): boolean {
        return (
            a.traderId !== b.traderId ||
            a.printerId !== b.printerId ||
            a.resourceId !== b.resourceId
        );
    }

    private matchesCurrentUser(userId: string | null): boolean {
        return !!this._currentUserId && !!userId && userId === this._currentUserId;
    }

    /**
     * Convert an assignment transition into the right action +
     * label and push it through the standard event-tile path —
     * which already handles the role gate, dedup, and pinning.
     *
     * `prevAssigneeId === currAssigneeId` is a no-op (we got here
     * because *something* changed, but the user-facing assignee
     * didn't — e.g. printer_id and trader_printer_id swapped but
     * the same person stayed primary). Drop those silently.
     */
    private emitAssignmentTile(
        order: any,
        prev: ReturnType<TopbarLiveFeedComponent['readAssignmentState']> | null,
        curr: ReturnType<TopbarLiveFeedComponent['readAssignmentState']>,
    ): void {
        const prevId = prev?.assigneeUserId ?? null;
        const currId = curr.assigneeUserId ?? null;
        const prevResource = prev?.resourceId ?? null;
        const currResource = curr.resourceId ?? null;

        let action: 'assigned_to_me' | 'unassigned_from_me' | 'assigned_out' | 'unassigned_out' | null = null;
        let label: string | null = null;

        if (currId && currId !== prevId) {
            // Newly assigned to a USER.
            if (this.matchesCurrentUser(currId)) {
                action = 'assigned_to_me';
                label = 'Assigned to Me';
            } else {
                action = 'assigned_out';
                label = this.buildAssignedLabel(order, curr);
            }
        } else if (!currId && prevId) {
            // User assignment removed (cleared, or rerouted to DP).
            if (this.matchesCurrentUser(prevId)) {
                action = 'unassigned_from_me';
                label = 'Unassigned from Me';
            } else if (currResource && currResource !== prevResource) {
                action = 'assigned_out';
                label = 'Routed to DP';
            } else {
                action = 'unassigned_out';
                label = 'Unassigned';
            }
        } else if (!currId && !prevId && currResource && currResource !== prevResource) {
            // Pure DP routing (no user assignment before or after).
            action = 'assigned_out';
            label = 'Routed to DP';
        } else if (!currId && !currResource && prevResource) {
            // DP routing cleared with no user reassignment.
            action = 'unassigned_out';
            label = 'Unassigned';
        }

        if (!action) return;

        // Role gate — single chokepoint so both this path and the
        // optimistic-emit path go through the same rule.
        if (!this.shouldSurfaceAssignmentToCurrentUser(action)) return;

        const visuals = this.actionVisuals(action);
        this.pushEventTile({
            dismissKey: `order:${order.id}:${action}`,
            kind: 'order_event',
            icon: visuals.icon,
            accent: visuals.accent,
            badge: `#${order.id}`,
            title: order.name || `Order #${order.id}`,
            caption: label || visuals.caption,
        });
    }

    /**
     * Best-effort human label for an assigned-to-someone-else
     * tile. Falls back through the order's denormalized name
     * fields (`trader_name`, `printer_name`, `printerName`) so
     * the row reads "Assigned to Alice" when possible.
     */
    private buildAssignedLabel(order: any, curr: { traderId: string | null; printerId: string | null }): string {
        const name: string = (
            (curr.traderId && (order?.trader_name || order?.printerName)) ||
            (curr.printerId && (order?.printer_name || order?.printerName)) ||
            order?.printerName ||
            ''
        ).toString().trim();
        return name ? `Assigned to ${name}` : 'Assigned';
    }

    private isAssignmentAction(action: string): boolean {
        return (
            action === 'assigned_to_me' ||
            action === 'unassigned_from_me' ||
            action === 'assigned_out' ||
            action === 'unassigned_out'
        );
    }

    /**
     * The one rule, stated once:
     *   • Admin    → see every assignment/unassignment.
     *   • Trader   → see only events that involve themselves
     *                (assigned_to_me / unassigned_from_me).
     *   • Any other role → suppress all assignment tiles.
     *
     * Keeping this in one method lets the optimistic-emit path
     * (`handleOrderEvent`) and the cache-diff path
     * (`emitAssignmentTile`) share the same gate — so flipping
     * the policy later happens in exactly one place.
     */
    private shouldSurfaceAssignmentToCurrentUser(action: string): boolean {
        if (this.isAdmin()) return true;
        if (this.isTrader()) {
            return action === 'assigned_to_me' || action === 'unassigned_from_me';
        }
        return false;
    }

    private handleOrderEvent(order: any, kind: 'updated' | 'created'): void {
        if (!order || order.id == null) return;

        // Prefer the semantic action attached by the service caller —
        // tells us "Deposit Added" or "Invoice Refunded" rather than
        // just leaking the order's current status. This is what makes
        // a refund tile read differently from a payment tile even
        // though both leave the order in the same `Production` state.
        const topbarAction = order._topbarAction as
            | { action: string; label: string; icon?: string; accent?: StreamItem['accent'] }
            | undefined;

        if (topbarAction) {
            // Role gate for assignment events:
            //   • Admin sees every variant (assigned_to_me too —
            //     they may be the one assigning to themselves).
            //   • Trader only sees events involving themselves —
            //     i.e. assigned_to_me / unassigned_from_me.
            //   • Everybody else (Sales, etc.) sees nothing for
            //     assignment events. Same risk model as Trader.
            if (this.isAssignmentAction(topbarAction.action)) {
                if (!this.shouldSurfaceAssignmentToCurrentUser(topbarAction.action)) {
                    return;
                }
            }

            const visuals = this.actionVisuals(topbarAction.action);
            this.pushEventTile({
                dismissKey: `order:${order.id}:${topbarAction.action}`,
                kind: 'order_event',
                icon: topbarAction.icon || visuals.icon,
                accent: topbarAction.accent || visuals.accent,
                badge: `#${order.id}`,
                title: order.name || `Order #${order.id}`,
                caption: topbarAction.label || visuals.caption,
            });
            return;
        }

        const status = String(order.status || '').toLowerCase().trim();
        const map = this.orderStatusVisuals(status, kind);
        this.pushEventTile({
            dismissKey: `order:${order.id}:${kind === 'created' ? 'created' : status || 'updated'}`,
            kind: 'order_event',
            icon: map.icon,
            accent: map.accent,
            badge: `#${order.id}`,
            title: order.name || `Order #${order.id}`,
            caption: map.caption,
        });
    }

    /**
     * Map a semantic action id to its tile visuals. Keeps the
     * full vocabulary in one place so callers in services only
     * need to know the action id + a short label — the icon,
     * accent, and fallback caption come from here.
     *
     * When a caller supplies an explicit `icon` / `accent` on
     * the topbar-action payload, those override what's returned
     * here. Useful for one-off variants (e.g. "Full Refund" vs
     * "Refund Issued" sharing the same icon family).
     */
    private actionVisuals(
        action: string,
    ): { icon: string; accent: StreamItem['accent']; caption: string } {
        switch (action) {
            // Status transitions
            case 'created':
                return { icon: 'add_circle_outline', accent: 'success', caption: 'New Order' };
            case 'updated':
                return { icon: 'history', accent: 'neutral', caption: 'Updated' };
            case 'production':
                return { icon: 'precision_manufacturing', accent: 'info', caption: 'In Production' };
            case 'completed':
                return { icon: 'task_alt', accent: 'success', caption: 'Completed' };
            case 'shipped':
                return { icon: 'local_shipping', accent: 'success', caption: 'Shipped' };
            case 'delivered':
                return { icon: 'done_all', accent: 'success', caption: 'Delivered' };
            case 'cancelled':
                return { icon: 'cancel', accent: 'danger', caption: 'Cancelled' };

            // Financial
            case 'invoice_paid':
                return { icon: 'paid', accent: 'success', caption: 'Invoice Paid' };
            case 'invoice_sent':
                return { icon: 'send', accent: 'info', caption: 'Invoice Sent' };
            case 'invoice_created':
                return { icon: 'receipt_long', accent: 'info', caption: 'Invoice Created' };
            case 'invoice_refunded':
                return { icon: 'currency_exchange', accent: 'warning', caption: 'Refund Issued' };
            case 'invoice_cancelled':
                return { icon: 'block', accent: 'danger', caption: 'Invoice Cancelled' };
            case 'deposit_added':
                return { icon: 'savings', accent: 'success', caption: 'Deposit Added' };
            case 'final_payment':
                return { icon: 'price_check', accent: 'success', caption: 'Final Payment' };

            // Workflow checkpoints (the 4-icon production gate)
            case 'checkpoint_pay':
                return { icon: 'payments', accent: 'info', caption: 'Marked Paid' };
            case 'checkpoint_art':
                return { icon: 'palette', accent: 'info', caption: 'Art Sized' };
            case 'checkpoint_film':
                return { icon: 'movie', accent: 'info', caption: 'Film Ready' };
            case 'checkpoint_apparel':
                return { icon: 'checkroom', accent: 'info', caption: 'Apparel Received' };

            // Receiving / shipping ops
            case 'trader_received':
                return { icon: 'how_to_reg', accent: 'success', caption: 'Received by Creator' };
            case 'returned_to_creator':
                return { icon: 'assignment_return', accent: 'warning', caption: 'Returned to Creator' };
            case 'handed_off':
                return { icon: 'assignment_return', accent: 'warning', caption: 'Handed Off' };
            case 'received_at_dp':
                return { icon: 'move_to_inbox', accent: 'success', caption: 'Received at DP' };
            case 'resource_work_done':
                return { icon: 'task_alt', accent: 'success', caption: 'Resource Work Done' };
            case 'merchandise_picked_up':
                return { icon: 'inventory_2', accent: 'info', caption: 'Merchandise Picked Up' };
            case 'tracking_added':
                return { icon: 'qr_code_2', accent: 'info', caption: 'Tracking Added' };
            case 'label_voided':
                return { icon: 'cancel', accent: 'warning', caption: 'Label Voided' };
            case 'label_removed':
                return { icon: 'delete_outline', accent: 'warning', caption: 'Label Removed' };

            // Uber Direct
            case 'uber_booked':
                return { icon: 'directions_car', accent: 'info', caption: 'Uber Booked' };
            case 'uber_cancelled':
                return { icon: 'car_crash', accent: 'warning', caption: 'Uber Cancelled' };

            // Outside resource flows
            case 'resource_label':
                return { icon: 'local_shipping', accent: 'info', caption: 'Resource Label' };
            case 'resource_notified':
                return { icon: 'mail', accent: 'info', caption: 'Resource Notified' };

            // Assignment events. `assigned_to_me` uses a brighter
            // success accent so a Trader's own row stands out from
            // the admin's broader "this got routed somewhere" feed.
            case 'assigned_to_me':
                return { icon: 'assignment_ind', accent: 'success', caption: 'Assigned to Me' };
            case 'unassigned_from_me':
                return { icon: 'person_remove', accent: 'warning', caption: 'Unassigned from Me' };
            case 'assigned_out':
                return { icon: 'assignment_turned_in', accent: 'info', caption: 'Assigned' };
            case 'unassigned_out':
                return { icon: 'person_off', accent: 'warning', caption: 'Unassigned' };

            default:
                return { icon: 'history', accent: 'neutral', caption: 'Updated' };
        }
    }

    private orderStatusVisuals(
        status: string,
        kind: 'updated' | 'created',
    ): { icon: string; accent: StreamItem['accent']; caption: string } {
        if (kind === 'created') {
            return { icon: 'add_circle_outline', accent: 'success', caption: 'New Order' };
        }
        switch (status) {
            case 'processing':
                return { icon: 'edit_note', accent: 'warning', caption: 'Processing' };
            case 'production':
                return { icon: 'precision_manufacturing', accent: 'info', caption: 'In Production' };
            case 'completed':
            case 'complete':
                return { icon: 'task_alt', accent: 'success', caption: 'Completed' };
            case 'shipped':
                return { icon: 'local_shipping', accent: 'success', caption: 'Shipped' };
            case 'delivered':
                return { icon: 'done_all', accent: 'success', caption: 'Delivered' };
            case 'cancelled':
            case 'canceled':
                return { icon: 'cancel', accent: 'danger', caption: 'Cancelled' };
            default:
                return {
                    icon: 'history',
                    accent: 'neutral',
                    caption: status ? this.capitalize(status.replace(/_/g, ' ')) : 'Updated',
                };
        }
    }

    // ─────────────────────────────────────────────────────────────────
    // Event buffer — push / fold / pin / expire
    // ─────────────────────────────────────────────────────────────────

    /**
     * Drop a new tile into `_eventTiles`. If the same dismissKey is
     * already on the tape, bump its `count` and refresh `expiresAt`
     * instead of inserting a duplicate row — this is the fold-up
     * that keeps a chatty sender at one row with "+3" rather than
     * three identical rows.
     *
     * After push, pin the tile into the next visible slot of the
     * marquee so it enters the user's viewport on the next frame
     * instead of being parked at the off-screen-left head of the
     * sorted stream.
     */
    private pushEventTile(input: {
        dismissKey: string;
        kind: StreamItem['kind'];
        icon: string;
        accent: StreamItem['accent'];
        title: string;
        caption?: string;
        badge?: string;
        meta?: string;
    }): void {
        const key = input.dismissKey;
        const now = Date.now();
        const existing = this._eventTiles.get(key);
        const count = (existing?.count ?? 0) + 1;
        const arrivedAt = existing?.arrivedAt ?? now;

        const badge = count > 1
            ? `+${count}`
            : (input.badge ?? undefined);

        const item: StreamItem = {
            id: key,
            kind: input.kind,
            badge,
            title: input.title,
            caption: input.caption,
            meta: input.meta,
            icon: input.icon,
            accent: input.accent,
            // Use the LATEST arrival as the sort key so a re-ping
            // of an existing tile pops back to the head of the tape.
            sortKey: now,
        };

        const tile: EventTile = {
            item,
            arrivedAt,
            expiresAt: now + this.EVENT_TTL_MS,
            count,
            pinAfterId: existing?.pinAfterId ?? this.findRightmostVisibleId(),
            pinExpiresAt: existing?.pinExpiresAt ?? now + this.INJECT_PIN_MS,
        };

        this._eventTiles.set(key, tile);
        this.rebuildStream();
    }

    /**
     * Locate the data-id of the item element currently in the
     * rightmost visible slot of the marquee. Used to pin a fresh
     * tile so it enters the viewport from the right on the next
     * paint — instead of being added at the sorted head, which is
     * currently scrolled off-screen to the left.
     *
     * Returns null when nothing measurable is in view (initial
     * frame, mid-rebuild). Callers treat null as "no pin — let it
     * sort naturally to the head".
     */
    private findRightmostVisibleId(): string | null {
        const trackEl = this.trackEl?.nativeElement;
        if (!trackEl) return null;
        const viewportEl = trackEl.parentElement;
        if (!viewportEl) return null;

        const viewportRect = viewportEl.getBoundingClientRect();
        const itemEls = Array.from(
            trackEl.querySelectorAll('.tlf-tape__item[data-id]'),
        ) as HTMLElement[];

        let rightmostId: string | null = null;
        let rightmostEdge = -Infinity;
        for (const el of itemEls) {
            const rect = el.getBoundingClientRect();
            const visible =
                rect.left < viewportRect.right &&
                rect.right > viewportRect.left;
            if (visible && rect.right > rightmostEdge) {
                rightmostEdge = rect.right;
                rightmostId = el.getAttribute('data-id');
            }
        }
        return rightmostId;
    }

    /**
     * Apply pinned positions onto a sorted item list. Pinned tiles
     * are spliced in right after their `pinAfterId` so a freshly-
     * arrived event surfaces in the user's current view rather
     * than at the (off-screen) sorted head.
     *
     * Pins expire after `INJECT_PIN_MS` so the tile eventually
     * settles into its natural sort position.
     */
    private applyPinnedPositions(items: StreamItem[]): StreamItem[] {
        const pins: { id: string; afterId: string | null }[] = [];
        const now = Date.now();
        for (const [id, tile] of this._eventTiles) {
            if (!tile.pinAfterId || (tile.pinExpiresAt ?? 0) <= now) continue;
            pins.push({ id, afterId: tile.pinAfterId });
        }
        if (pins.length === 0) return items;

        let result = [...items];
        for (const pin of pins) {
            const idx = result.findIndex((x) => x.id === pin.id);
            if (idx === -1) continue;
            const [tile] = result.splice(idx, 1);
            let insertIdx = 0;
            if (pin.afterId) {
                const targetIdx = result.findIndex((x) => x.id === pin.afterId);
                insertIdx = targetIdx >= 0 ? targetIdx + 1 : 0;
            }
            result.splice(insertIdx, 0, tile);
        }
        return result;
    }

    // ─────────────────────────────────────────────────────────────────
    // Merge — produces the rendered `streamItems`
    // ─────────────────────────────────────────────────────────────────
    private rebuildStream(): void {
        const now = Date.now();
        this.now = now;

        // 1. Sweep expired event tiles before doing anything else
        //    so they don't end up in the rendered stream.
        let mutated = false;
        for (const [key, tile] of this._eventTiles) {
            if (tile.expiresAt <= now) {
                this._eventTiles.delete(key);
                mutated = true;
            }
        }
        // (no-op mutated check — we're rebuilding anyway, just useful
        // signal for future debugging.)
        void mutated;

        const items: StreamItem[] = [];

        // 2. Event tiles — these always sort to the head (their
        //    sortKey is `Date.now()` at arrival, problem orders
        //    use a negative sortKey).
        for (const tile of this._eventTiles.values()) {
            items.push(tile.item);
        }

        // 3. Problem orders — fill the tape with persistent issues
        //    so it's never empty, but with a negative sortKey so
        //    events always lead when present.
        for (const order of this._updates) {
            items.push(this.toUpdateItem(order));
        }

        // 4. Empty-state placeholders if literally nothing.
        if (items.length === 0) {
            items.push(
                this._updatesLoaded
                    ? {
                          id: 'empty-all-clear',
                          kind: 'update',
                          title: 'All caught up',
                          caption: 'Quiet',
                          icon: 'verified',
                          accent: 'success',
                          sortKey: 0,
                      }
                    : {
                          id: 'empty-loading',
                          kind: 'update',
                          title: 'Loading live updates…',
                          caption: 'Sync',
                          icon: 'hourglass_empty',
                          accent: 'neutral',
                          sortKey: 0,
                      },
            );
        }

        // 5. Sort highest first (events lead by virtue of their
        //    positive arrival-time sortKey; updates trail at <=0).
        items.sort((a, b) => b.sortKey - a.sortKey);

        // 6. Apply next-slot pins so a freshly-arrived event lands
        //    in the user's CURRENT viewport instead of being
        //    parked off-screen at the sorted head.
        const positioned = this.applyPinnedPositions(items);

        this.streamItems = positioned.slice(0, this.MAX_TOTAL);
        this.cdr.markForCheck();
    }

    // ─────────────────────────────────────────────────────────────────
    // Problem-order projector — negative sortKey so events always
    // lead. Within problem orders, danger > warning > info > neutral
    // (same priority ordering as before).
    // ─────────────────────────────────────────────────────────────────
    private toUpdateItem(order: UpdateRequiredOrder): StreamItem {
        const issueText = (order?.issueStatus?.text || '').trim();
        const lower = issueText.toLowerCase();

        let accent: StreamItem['accent'] = 'warning';
        let icon = 'warning_amber';

        if (lower.includes('not shipped')) {
            accent = 'danger';
            icon = 'local_shipping';
        } else if (lower.includes('past') || lower.includes('overdue')) {
            accent = 'danger';
            icon = 'event_busy';
        } else if (lower.includes('incomplete')) {
            accent = 'warning';
            icon = 'report_problem';
        } else if (lower.includes('approaching')) {
            accent = 'warning';
            icon = 'schedule';
        } else if (lower.includes('missing')) {
            accent = 'info';
            icon = 'checklist';
        }

        // Negative range. Danger problems sort closer to 0 (i.e.
        // higher) so they're the first thing the user sees once
        // the live-event head has scrolled past.
        const accentBoost =
            accent === 'danger' ? 4
            : accent === 'warning' ? 3
            : accent === 'info' ? 2
            : 1;
        const sortKey = -1_000_000 + accentBoost * 1_000;

        return {
            id: `u-${order.id ?? ''}`,
            kind: 'update',
            badge: order.id != null ? `#${order.id}` : undefined,
            title: order?.name || `Order #${order?.id ?? '—'}`,
            caption: issueText || 'Needs attention',
            meta: this.formatDueLabel(order?.arrival_date),
            icon,
            accent,
            sortKey,
        };
    }

    // ─────────────────────────────────────────────────────────────────
    // Time-ago tick — drives expiry sweeps + meta refresh.
    // ─────────────────────────────────────────────────────────────────
    private startTick(): void {
        this.timeAgoSub = interval(this.TIME_AGO_MS).subscribe(() => {
            this.now = Date.now();
            this.rebuildStream();
        });
    }

    private formatDueLabel(arrival?: string): string | undefined {
        if (!arrival) return undefined;
        const parts = String(arrival).split(/[-T]/);
        let d: Date;
        if (parts.length >= 3) {
            d = new Date(
                parseInt(parts[0], 10),
                parseInt(parts[1], 10) - 1,
                parseInt(parts[2], 10),
            );
        } else {
            d = new Date(arrival);
        }
        if (Number.isNaN(d.getTime())) return undefined;
        return `Due ${d.toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
        })}`;
    }

    private truncate(s: string, max: number): string {
        if (!s) return s;
        if (s.length <= max) return s;
        return s.slice(0, max - 1).trimEnd() + '…';
    }

    private capitalize(s: string): string {
        if (!s) return s;
        return s.charAt(0).toUpperCase() + s.slice(1);
    }
}
